import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { B2BInvoice, B2bInvoiceService } from '../../../services/b2b-invoice.service';
import { ContractsService } from '../../../services/contracts.service';
import { ConfirmationService } from '../../../shared/services/confirmation.service';
import { ErrorService } from '../../../shared/services/error.service';
import { TableAction } from '../../../shared/components/table/table.component';

@Component({
  selector: 'app-b2b-invoices',
  templateUrl: './b2b-invoices.component.html',
  styleUrls: ['../shared/b2b-operational-shared.css']
})
export class B2bInvoicesComponent implements OnInit {
  contractId: number | null = null;
  contractNumber = '';
  contractName = '';
  companyName = '';
  customerName = '';
  items: B2BInvoice[] = [];
  isLoading = false;
  filterYear: number | null = null;
  filterMonth: number | null = null;
  filterStatus = '';
  filterCategory = '';

  readonly statusOptions = [
    { value: '', label: 'Todos' },
    { value: 'Draft', label: 'Rascunho' },
    { value: 'Issued', label: 'Emitida' },
    { value: 'Paid', label: 'Paga' },
    { value: 'Cancelled', label: 'Cancelada' }
  ];

  readonly categoryOptions = [
    { value: '', label: 'Todas' },
    { value: 'Rental', label: 'Aluguel' },
    { value: 'Reserve', label: 'Moto reserva' },
    { value: 'TrafficFine', label: 'Multas' },
    { value: 'Damage', label: 'Avarias' },
    { value: 'Deposit', label: 'Caução' },
    { value: 'Claim', label: 'Sinistro' },
    { value: 'Telemetry', label: 'Telemetria' },
    { value: 'Accessory', label: 'Acessórios' }
  ];

  columns = [
    { titulo: 'Número', label: 'invoiceNumber' },
    { titulo: 'Competência', label: 'competenceLabel' },
    { titulo: 'Categoria', label: 'categoryLabel' },
    { titulo: 'Status', label: 'statusLabel' },
    { titulo: 'Valor', label: 'totalAmount', formatter: (v: any) => this.formatMoney(v), sensitive: true },
    { titulo: 'Vencimento', label: 'dueDate', formatter: (v: any) => this.formatDate(v) }
  ];

  actions: TableAction[] = [
    { label: 'Visualizar', icon: 'fas fa-eye', action: 'view', class: 'btn-view' },
    { label: 'Emitir', icon: 'fas fa-check', action: 'issue', class: 'btn-edit' },
    { label: 'Ver Contas a Receber', icon: 'fas fa-file-invoice-dollar', action: 'accountsReceivable', class: 'btn-view' },
    { label: 'Cancelar', icon: 'fas fa-ban', action: 'cancel', class: 'btn-delete' }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private invoiceService: B2bInvoiceService,
    private contractsService: ContractsService,
    private errorService: ErrorService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.contractId = +params['id'];
      if (this.contractId) {
        this.loadContractInfo();
        this.loadItems();
      }
    });
  }

  loadContractInfo(): void {
    if (!this.contractId) return;
    this.contractsService.getB2bContractById(this.contractId).subscribe({
      next: (c: any) => {
        this.contractNumber = c.contractNumber || c.ContractNumber || '';
        this.contractName = c.contractName || c.ContractName || '';
        this.companyName = c.companyName || c.CompanyName || '';
        this.customerName = c.customerName || c.CustomerName || '';
      },
      error: (err) => this.errorService.handleHttpError(err)
    });
  }

  get contractDisplayName(): string {
    return this.companyName || this.customerName || this.contractName || this.contractNumber || '';
  }

  loadItems(): void {
    if (!this.contractId) return;
    this.isLoading = true;
    this.invoiceService.listByContract(this.contractId, {
      year: this.filterYear || undefined,
      month: this.filterMonth || undefined,
      category: this.filterCategory || undefined,
      status: this.filterStatus || undefined
    }).subscribe({
      next: (list) => {
        this.items = (list || []).map(x => this.normalize(x));
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        this.errorService.handleHttpError(err);
      }
    });
  }

  applyFilter(): void {
    this.loadItems();
  }

  navigateBack(): void {
    if (this.contractId) {
      this.router.navigate(['/b2b/contracts', this.contractId]);
    }
  }

  onActionClick(event: { action: string; item: any }): void {
    const invoice = event.item as B2BInvoice;
    if (event.action === 'view') {
      this.openDetailPage(invoice.id);
      return;
    }
    if (event.action === 'issue') {
      this.issue(invoice);
      return;
    }
    if (event.action === 'accountsReceivable') {
      this.openAccountsReceivable(invoice);
      return;
    }
    if (event.action === 'cancel') {
      this.cancel(invoice);
    }
  }

  openDetailPage(invoiceId: number): void {
    if (!this.contractId) return;
    this.router.navigate(['/b2b/contracts', this.contractId, 'faturas', invoiceId]);
  }

  issue(invoice: B2BInvoice): void {
    if (invoice.status !== 'Draft') {
      this.errorService.showError('Só é possível emitir fatura em rascunho.');
      return;
    }
    this.confirmationService.confirm({
      title: 'Emitir fatura',
      message: `Emitir ${invoice.invoiceNumber} e criar apenas o Contas a Receber (sem Asaas)? O espelho fica disponível no portal após a emissão.`,
      confirmText: 'Emitir'
    }).subscribe({
      next: (ok) => {
        if (!ok) return;
        this.invoiceService.issue(invoice.id).subscribe({
          next: (issued) => {
            const normalized = this.normalize(issued);
            this.errorService.showSuccessMessage('Fatura emitida. Contas a Receber criado (Asaas desligado neste fluxo).');
            this.loadItems();
            this.openDetailPage(normalized.id);
          },
          error: (err) => this.errorService.handleHttpError(err)
        });
      }
    });
  }

  cancel(invoice: B2BInvoice): void {
    if (invoice.status === 'Paid' || invoice.status === 'Cancelled') {
      this.errorService.showError('Esta fatura não pode ser cancelada.');
      return;
    }
    this.confirmationService.confirm({
      title: 'Cancelar fatura',
      message: invoice.accountsReceivableId
        ? `Cancelar ${invoice.invoiceNumber} e o Contas a Receber #${invoice.accountsReceivableId}?`
        : `Cancelar ${invoice.invoiceNumber}?`,
      confirmText: 'Cancelar fatura'
    }).subscribe({
      next: (ok) => {
        if (!ok) return;
        this.invoiceService.cancel(invoice.id).subscribe({
          next: () => {
            this.errorService.showSuccessMessage('Fatura cancelada.');
            this.loadItems();
          },
          error: (err) => this.errorService.handleHttpError(err)
        });
      }
    });
  }

  openAccountsReceivable(invoice: B2BInvoice): void {
    const arId = invoice.accountsReceivableId;
    if (!arId) {
      this.errorService.showError('Esta fatura ainda não possui Contas a Receber. Emita a fatura primeiro.');
      return;
    }
    if (invoice.status === 'Draft' || invoice.status === 'Cancelled') {
      this.errorService.showError('Só é possível abrir Contas a Receber de fatura emitida.');
      return;
    }
    this.router.navigate(['/financial/accounts-receivable', arId, 'view']);
  }

  goBillingClosing(): void {
    if (!this.contractId) return;
    this.router.navigate(['/b2b/billing-closing'], {
      queryParams: { contractId: this.contractId, wizard: '1' }
    });
  }

  goPriceTable(): void {
    if (this.contractId) {
      this.router.navigate(['/b2b/contracts', this.contractId, 'precos']);
    }
  }

  private normalize(raw: any): B2BInvoice {
    const inv: B2BInvoice = {
      id: raw.id ?? raw.Id,
      b2BContractId: raw.b2BContractId ?? raw.B2BContractId,
      contractNumber: raw.contractNumber ?? raw.ContractNumber,
      contractName: raw.contractName ?? raw.ContractName,
      companyName: raw.companyName ?? raw.CompanyName,
      customerId: raw.customerId ?? raw.CustomerId,
      competenceYear: raw.competenceYear ?? raw.CompetenceYear,
      competenceMonth: raw.competenceMonth ?? raw.CompetenceMonth,
      category: raw.category ?? raw.Category,
      categoryLabel: raw.categoryLabel ?? raw.CategoryLabel,
      status: raw.status ?? raw.Status,
      invoiceNumber: raw.invoiceNumber ?? raw.InvoiceNumber,
      totalAmount: Number(raw.totalAmount ?? raw.TotalAmount ?? 0),
      dueDate: raw.dueDate ?? raw.DueDate,
      issuedAt: raw.issuedAt ?? raw.IssuedAt,
      paidAt: raw.paidAt ?? raw.PaidAt,
      accountsReceivableId: raw.accountsReceivableId ?? raw.AccountsReceivableId,
      notes: raw.notes ?? raw.Notes,
      createdAt: raw.createdAt ?? raw.CreatedAt,
      items: (raw.items ?? raw.Items ?? []).map((i: any) => ({
        id: i.id ?? i.Id,
        plate: i.plate ?? i.Plate,
        description: i.description ?? i.Description,
        reference: i.reference ?? i.Reference,
        days: i.days ?? i.Days,
        unitAmount: i.unitAmount ?? i.UnitAmount,
        amount: Number(i.amount ?? i.Amount ?? 0),
        sourceType: i.sourceType ?? i.SourceType,
        sourceId: i.sourceId ?? i.SourceId,
        periodStart: i.periodStart ?? i.PeriodStart,
        periodEnd: i.periodEnd ?? i.PeriodEnd
      }))
    };
    (inv as any).competenceLabel = `${String(inv.competenceMonth).padStart(2, '0')}/${inv.competenceYear}`;
    (inv as any).statusLabel = this.statusLabel(inv.status);
    return inv;
  }

  statusLabel(status: string): string {
    return this.statusOptions.find(s => s.value === status)?.label || status;
  }

  formatMoney(v: any): string {
    const n = Number(v);
    if (isNaN(n)) return '—';
    return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  formatDate(v: any): string {
    if (!v) return '—';
    const d = new Date(v);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('pt-BR');
  }
}
